/**
 * A table as a PDF, written without a library: Helvetica (a standard PDF font, so nothing is
 * embedded), A4 pages (landscape when the columns need it), the header row on every page, bold
 * group headings, page numbers. Text is WinAnsi (Western European); other characters show as "?".
 */
import type { CsvValue } from './csv';
import { plainCell } from './csv';
import type { ExportGroup } from './tableExport';

export interface PdfTable {
  title: string;
  /** One line under the title: what the rows are, when they were exported. */
  subtitle?: string;
  headers: readonly string[];
  rows: readonly (readonly CsvValue[])[];
  /** Contiguous groups of rows; each gets a heading row above it. */
  groups?: readonly ExportGroup[];
}

const A4 = [595.28, 841.89] as const;
const MARGIN = 40;
const PAD = 5;
const TITLE_SIZE = 14;
const SUB_SIZE = 8.5;
const BODY_SIZE = 8.5;
const MIN_BODY_SIZE = 6.5;
const FOOTER_SIZE = 7.5;
const FOOTER_H = 18;
/** A column narrower than this is never squeezed when the table is wider than the page. */
const NARROW = 58;

/* ---------- Text metrics and encoding ---------- */

/** Helvetica advance widths (1/1000 em) of the printable ASCII characters, space to tilde. */
const WIDTHS = (
  '278 278 355 556 556 889 667 191 333 333 389 584 278 333 278 278 ' +
  '556 556 556 556 556 556 556 556 556 556 278 278 584 584 584 556 ' +
  '1015 667 667 722 722 667 611 778 722 278 500 667 556 833 722 778 ' +
  '667 778 722 667 611 722 667 944 667 667 611 278 278 278 469 556 ' +
  '333 556 556 500 556 556 278 556 556 222 222 500 222 833 556 556 ' +
  '556 556 333 500 278 556 500 722 500 500 500 334 260 334 584'
)
  .split(' ')
  .map(Number);
const DEFAULT_WIDTH = 556;
const BOLD_FACTOR = 1.06;

/** Width in points of text set in Helvetica at the size. */
export function helveticaWidth(text: string, size: number, bold = false): number {
  let units = 0;
  for (const char of shown(text)) {
    const code = char.charCodeAt(0);
    units += code >= 32 && code <= 126 ? (WIDTHS[code - 32] ?? DEFAULT_WIDTH) : DEFAULT_WIDTH;
  }
  return (units / 1000) * size * (bold ? BOLD_FACTOR : 1);
}

/** Characters above Latin-1 that WinAnsi encodes, by code page 1252 byte. */
const CP1252: Record<string, number> = {
  '€': 0x80,
  '‚': 0x82,
  ƒ: 0x83,
  '„': 0x84,
  '…': 0x85,
  '†': 0x86,
  '‡': 0x87,
  ˆ: 0x88,
  '‰': 0x89,
  Š: 0x8a,
  '‹': 0x8b,
  Œ: 0x8c,
  Ž: 0x8e,
  '‘': 0x91,
  '’': 0x92,
  '“': 0x93,
  '”': 0x94,
  '•': 0x95,
  '–': 0x96,
  '—': 0x97,
  '˜': 0x98,
  '™': 0x99,
  š: 0x9a,
  '›': 0x9b,
  œ: 0x9c,
  ž: 0x9e,
  Ÿ: 0x9f,
};
/**
 * Characters WinAnsi lacks, with their stand-ins; the euro sign is in WinAnsi, but viewers draw
 * the standard fonts with substitutes whose euro glyph overruns its advance, so it is spelt out.
 */
const SUBSTITUTES: Record<string, string> = {
  '\u2212': '-',
  '\u2060': '',
  '\u00a0': ' ',
  '\u20ac': 'EUR',
};
/** Text as the PDF shows it: the substitutes applied, so widths are measured on the same text. */
const shown = (text: string) =>
  text.replace(/[\u2212\u2060\u00a0\u20ac]/g, (char) => SUBSTITUTES[char] ?? char);

/** The text as WinAnsi bytes in a PDF literal string, escaped. */
function literal(text: string): string {
  let out = '';
  for (const c of shown(text)) {
    const code = c.charCodeAt(0);
    const byte = code < 256 ? code : (CP1252[c] ?? 63);
    if (byte === 40 || byte === 41 || byte === 92) out += `\\${String.fromCharCode(byte)}`;
    else if (byte < 32 || byte > 126) out += `\\${byte.toString(8).padStart(3, '0')}`;
    else out += String.fromCharCode(byte);
  }
  return `(${out})`;
}

/* ---------- Cell text ---------- */

const numberFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

export function pdfCellText(value: CsvValue): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? numberFormat.format(value) : '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return shown(plainCell(value)).replace(/\s+/g, ' ').trim();
}

/** The text, or as much of it as fits the width, ending in an ellipsis. */
function fit(text: string, width: number, size: number, bold = false): string {
  if (helveticaWidth(text, size, bold) <= width) return text;
  const ellipsis = '…';
  let end = text.length;
  while (end > 0 && helveticaWidth(text.slice(0, end) + ellipsis, size, bold) > width) end -= 1;
  return end === 0 ? '' : text.slice(0, end).trimEnd() + ellipsis;
}

/* ---------- Layout ---------- */

type Item = { kind: 'group'; label: string } | { kind: 'row'; cells: readonly string[] };

const sum = (values: readonly number[]) => values.reduce((total, v) => total + v, 0);
const n = (value: number) => (Math.round(value * 100) / 100).toString();

interface Layout {
  landscape: boolean;
  pageW: number;
  pageH: number;
  size: number;
  widths: number[];
  rowH: number;
  headH: number;
  groupH: number;
}

function layout(headers: readonly string[], items: Item[]): Layout {
  const portraitW = A4[0] - 2 * MARGIN;
  const landscapeW = A4[1] - 2 * MARGIN;
  const natural = (size: number) =>
    headers.map((header, i) => {
      let widest = helveticaWidth(header, size, true);
      for (const item of items) {
        if (item.kind === 'row')
          widest = Math.max(widest, helveticaWidth(item.cells[i] ?? '', size));
      }
      return widest + 2 * PAD;
    });
  let size = BODY_SIZE;
  let widths = natural(size);
  let total = sum(widths);
  const landscape = total > portraitW;
  const contentW = landscape ? landscapeW : portraitW;
  if (total > contentW) {
    size = Math.max(MIN_BODY_SIZE, (size * contentW) / total);
    widths = natural(size);
    total = sum(widths);
  }
  if (total > contentW) {
    // Columns up to the floor keep their width; the wider ones share what is left.
    const floor = widths.map((w) => Math.min(w, NARROW));
    const spare = contentW - sum(floor);
    const stretch = sum(widths.map((w, i) => w - (floor[i] ?? 0)));
    widths =
      spare > 0 && stretch > 0
        ? widths.map((w, i) => (floor[i] ?? 0) + ((w - (floor[i] ?? 0)) * spare) / stretch)
        : widths.map((w) => (w * contentW) / total);
  } else if (total < contentW) {
    const factor = Math.min(1.6, contentW / total);
    widths = widths.map((w) => w * factor);
  }
  return {
    landscape,
    pageW: landscape ? A4[1] : A4[0],
    pageH: landscape ? A4[0] : A4[1],
    size,
    widths,
    rowH: size * 1.8,
    headH: size * 2.1,
    groupH: size * 2,
  };
}

/** Baseline of text of the size centred in a row that starts at `top` and is `height` tall. */
const baseline = (top: number, height: number, size: number) =>
  top - height + (height - size) / 2 + size * 0.24;

interface PageText {
  content: string;
}

function renderPage(
  table: PdfTable,
  lay: Layout,
  items: Item[],
  right: readonly boolean[],
  page: number,
  pages: number,
): PageText {
  const { pageW, pageH, size, widths, rowH, headH, groupH } = lay;
  const left = MARGIN;
  // The table may be narrower than the page; the title, subtitle and footer use the page.
  const pageContentW = pageW - 2 * MARGIN;
  const contentW = sum(widths);
  const ops: string[] = [];
  const text = (x: number, y: number, str: string, pt: number, bold = false, gray = 0) => {
    if (!str) return;
    ops.push(
      `BT /F${bold ? 2 : 1} ${n(pt)} Tf ${n(gray)} g 1 0 0 1 ${n(x)} ${n(y)} Tm ${literal(str)} Tj ET`,
    );
  };
  const line = (y: number, gray: number, w: number) => {
    ops.push(`${n(gray)} G ${n(w)} w ${n(left)} ${n(y)} m ${n(left + contentW)} ${n(y)} l S`);
  };
  const fill = (y: number, h: number, gray: number) => {
    ops.push(`${n(gray)} g ${n(left)} ${n(y - h)} ${n(contentW)} ${n(h)} re f`);
  };
  const cellX = (i: number, str: string, bold: boolean) => {
    const start = left + sum(widths.slice(0, i));
    const width = widths[i] ?? 0;
    return right[i] ? start + width - PAD - helveticaWidth(str, size, bold) : start + PAD;
  };

  let y = pageH - MARGIN;
  // Title block on every page, so a page on its own still says what it is.
  text(left, y - TITLE_SIZE, fit(table.title, pageContentW, TITLE_SIZE, true), TITLE_SIZE, true);
  y -= TITLE_SIZE + 6;
  if (table.subtitle) {
    text(left, y - SUB_SIZE, fit(table.subtitle, pageContentW, SUB_SIZE), SUB_SIZE, false, 0.4);
    y -= SUB_SIZE + 6;
  }
  y -= 6;
  // Header row.
  fill(y, headH, 0.92);
  table.headers.forEach((header, i) => {
    const str = fit(header, (widths[i] ?? 0) - 2 * PAD, size, true);
    text(cellX(i, str, true), baseline(y, headH, size), str, size, true);
  });
  y -= headH;
  line(y, 0.6, 0.6);
  for (const item of items) {
    if (item.kind === 'group') {
      fill(y, groupH, 0.96);
      const str = fit(item.label, contentW - 2 * PAD, size, true);
      text(left + PAD, baseline(y, groupH, size), str, size, true);
      y -= groupH;
      line(y, 0.75, 0.4);
      continue;
    }
    item.cells.forEach((cell, i) => {
      const str = fit(cell, (widths[i] ?? 0) - 2 * PAD, size);
      text(cellX(i, str, false), baseline(y, rowH, size), str, size);
    });
    y -= rowH;
    line(y, 0.85, 0.3);
  }
  // Footer.
  const footerY = MARGIN - 2;
  text(left, footerY, fit(table.title, pageContentW * 0.6, FOOTER_SIZE), FOOTER_SIZE, false, 0.45);
  const pageText = `Page ${page} of ${pages}`;
  text(
    left + pageContentW - helveticaWidth(pageText, FOOTER_SIZE),
    footerY,
    pageText,
    FOOTER_SIZE,
    false,
    0.45,
  );
  return { content: ops.join('\n') };
}

/** Splits the items into pages by the heights the layout gives them. */
function paginate(table: PdfTable, lay: Layout, items: Item[]): Item[][] {
  const { pageH, rowH, headH, groupH } = lay;
  const titleBlock = TITLE_SIZE + 6 + (table.subtitle ? SUB_SIZE + 6 : 0) + 6;
  const available = pageH - 2 * MARGIN - titleBlock - headH - FOOTER_H;
  const pages: Item[][] = [];
  let page: Item[] = [];
  let used = 0;
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (!item) continue;
    const h = item.kind === 'group' ? groupH : rowH;
    // A group heading never ends a page: it moves to the next one with its first row.
    const next = items[i + 1];
    const need = item.kind === 'group' && next?.kind === 'row' ? h + rowH : h;
    if (used + need > available && page.length > 0) {
      pages.push(page);
      page = [];
      used = 0;
    }
    page.push(item);
    used += h;
  }
  if (page.length > 0 || pages.length === 0) pages.push(page);
  return pages;
}

const pdfDate = (date: Date) =>
  `D:${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}` +
  `${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}${String(date.getSeconds()).padStart(2, '0')}`;

/** The PDF as bytes. */
export function buildPdf(table: PdfTable, date = new Date()): Uint8Array {
  const rows = table.rows.map((row) => table.headers.map((_, i) => pdfCellText(row[i])));
  const right = table.headers.map(
    (_, i) =>
      table.rows.some((row) => typeof row[i] === 'number') &&
      table.rows.every((row) => {
        const v = row[i];
        return v === null || v === undefined || v === '' || typeof v === 'number';
      }),
  );
  const items: Item[] = [];
  if (table.groups && table.groups.length > 0) {
    for (const group of table.groups) {
      items.push({ kind: 'group', label: `${group.label} (${group.count})` });
      for (const cells of rows.slice(group.start, group.start + group.count))
        items.push({ kind: 'row', cells });
    }
  } else {
    for (const cells of rows) items.push({ kind: 'row', cells });
  }
  const lay = layout(table.headers, items);
  const pages = paginate(table, lay, items);

  // Objects: 1 catalog, 2 pages, 3 font, 4 bold font, 5 info, then a page and a stream per page.
  const objects: string[] = [];
  const pageIds = pages.map((_, i) => 6 + i * 2);
  objects.push('<< /Type /Catalog /Pages 2 0 R >>');
  objects.push(
    `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pages.length} >>`,
  );
  objects.push('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');
  objects.push(
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>',
  );
  objects.push(
    `<< /Title ${literal(table.title)} /Producer (Open as App KPI portal) /CreationDate (${pdfDate(date)}) >>`,
  );
  pages.forEach((items, index) => {
    const { content } = renderPage(table, lay, items, right, index + 1, pages.length);
    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${n(lay.pageW)} ${n(lay.pageH)}] ` +
        `/Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${7 + index * 2} 0 R >>`,
    );
    objects.push(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
  });

  let out = '%PDF-1.4\n%âãÏÓ\n';
  const offsets: number[] = [];
  objects.forEach((body, i) => {
    offsets.push(out.length);
    out += `${i + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xref = out.length;
  out += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (const offset of offsets) out += `${String(offset).padStart(10, '0')} 00000 n \n`;
  out += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R /Info 5 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  return Uint8Array.from(out, (char) => char.charCodeAt(0));
}
